/**
 * PhaseHandoffBar — top-of-workspace bar showing the project's current phase,
 * phase target progress, and buttons to:
 *   - Download .rachana package (zip or json)
 *   - Upload .rachana on import
 *   - Advance to the next phase (in-place upgrade)
 *
 * Implements `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §4, §7.
 */
import React, { useEffect, useRef, useState } from "react";
import { Download, Upload, ChevronRight, Loader2, Layers, FileText, Package } from "lucide-react";
import api, { formatErr } from "../lib/api";

const PHASE_LABELS = {
  concept: "Concept",
  pre_feed: "Pre-FEED",
  feed: "FEED",
  detail: "Detailed Engineering",
};
const PHASE_ORDER = ["concept", "pre_feed", "feed", "detail"];

export default function PhaseHandoffBar({ project, onPhaseChanged }) {
  const [visibility, setVisibility] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!project?.id || !project?.phase) return;
    (async () => {
      try {
        const r = await api.get(`/projects/${project.id}/phase/visibility`);
        setVisibility(r.data);
      } catch { /* ignore */ }
    })();
  }, [project?.id, project?.phase]);

  if (!project || !project.phase) return null;
  const next = visibility?.next_phase;
  const currentIdx = PHASE_ORDER.indexOf(project.phase);

  const show = (text, kind = "ok") => {
    setToast({ text, kind });
    setTimeout(() => setToast(null), 4500);
  };

  const advance = async () => {
    if (!next) return;
    if (!window.confirm(`Advance project to ${PHASE_LABELS[next]}?\n\nAll data is preserved — sub-stages unlocked by the new phase will become active.`)) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/phase/advance`, {
        from_phase: project.phase,
        to_phase: next,
      });
      show(`Phase advanced → ${PHASE_LABELS[next]}`);
      onPhaseChanged?.(next);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const download = async (fmt) => {
    setBusy(true);
    try {
      const resp = await api.get(`/projects/${project.id}/phase/package?format=${fmt}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data],
        { type: fmt === "zip" ? "application/zip" : "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (project.name || "project").replace(/\s+/g, "_");
      a.href = url;
      a.download = `${safe}_${project.phase}.rachana.${fmt === "zip" ? "zip" : "json"}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      show(`Downloaded ${project.phase}.rachana.${fmt}`);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/projects/${project.id}/phase/import`, form);
      const body = r.data;
      show(
        `Imported: ${(body.imported || []).join(", ")}. ` +
        `PDO sections: ${(body.pdo_sections_populated || []).length}. ` +
        `Sheets: ${body.sheet_count}. Source phase: ${body.source_phase || "—"}`
      );
      // Reload after import
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const feasibilityReport = async () => {
    setBusy(true);
    try {
      const resp = await api.post(`/projects/${project.id}/phase/feasibility-report`,
                                    {}, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (project.name || "project").replace(/\s+/g, "_");
      a.href = url; a.download = `${safe}_feasibility.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      show("Feasibility Report PDF generated");
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const deliverablesBundle = async () => {
    setBusy(true);
    try {
      const resp = await api.get(`/projects/${project.id}/phase/deliverables-bundle`,
                                    { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (project.name || "project").replace(/\s+/g, "_");
      a.href = url; a.download = `${safe}_deliverables.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      show("Deliverables bundle (PDFs + Excel + SVG) downloaded");
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="phase-handoff-bar" style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "8px 16px",
      background: "linear-gradient(90deg, rgba(255,176,0,0.06), rgba(255,176,0,0.02))",
      borderBottom: "1px solid var(--pf-border)",
      fontSize: 12,
    }}>
      <Layers size={14} color="var(--pf-accent)" />
      <span className="pf-overline" style={{ margin: 0, color: "var(--pf-accent)" }}>PHASE</span>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {PHASE_ORDER.map((p, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <React.Fragment key={p}>
              <span
                data-testid={`phase-chip-${p}`}
                data-active={active ? "true" : "false"}
                style={{
                  padding: "3px 10px",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  borderRadius: 2,
                  background: active ? "var(--pf-accent)" : (done ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.04)"),
                  color: active ? "#0E1218" : (done ? "#86efac" : "var(--pf-text-dim)"),
                  fontWeight: active ? 700 : 500,
                  border: "1px solid " + (active ? "var(--pf-accent)" : (done ? "#22c55e33" : "var(--pf-border)")),
                }}
              >{done ? "✓ " : ""}{PHASE_LABELS[p]}</span>
              {i < PHASE_ORDER.length - 1 && <ChevronRight size={10} color="var(--pf-text-dim)" />}
            </React.Fragment>
          );
        })}
      </div>

      <span style={{ marginLeft: 8, color: "var(--pf-text-dim)", fontFamily: "'JetBrains Mono', monospace" }}>
        Cost accuracy {visibility?.cost_accuracy || "—"}
      </span>

      <div style={{ flex: 1 }} />

      <button
        data-testid="phase-download-zip-btn"
        className="btn btn-ghost"
        onClick={() => download("zip")}
        disabled={busy}
        style={{ fontSize: 11, padding: "4px 10px" }}
        title="Download phase handoff package (.rachana.zip)"
      ><Download size={12} /> .rachana .zip</button>

      <button
        data-testid="phase-download-json-btn"
        className="btn btn-ghost"
        onClick={() => download("json")}
        disabled={busy}
        style={{ fontSize: 11, padding: "4px 10px" }}
        title="Download phase handoff package (.rachana.json)"
      ><Download size={12} /> .json</button>

      <button
        data-testid="phase-feasibility-report-btn"
        className="btn btn-ghost"
        onClick={feasibilityReport}
        disabled={busy}
        style={{ fontSize: 11, padding: "4px 10px" }}
        title="Generate Feasibility / FEED Report PDF (spec §4.1)"
      ><FileText size={12} /> Feasibility PDF</button>

      <button
        data-testid="phase-deliverables-bundle-btn"
        className="btn btn-ghost"
        onClick={deliverablesBundle}
        disabled={busy}
        style={{ fontSize: 11, padding: "4px 10px" }}
        title="Download PDFs + Excel + SVG deliverables bundle (spec §4.1)"
      ><Package size={12} /> PDFs + Excel</button>

      <input
        ref={fileRef}
        type="file"
        accept=".zip,.json,.rachana"
        style={{ display: "none" }}
        data-testid="phase-import-input"
        onChange={(e) => {
          const f = e.target.files?.[0]; if (f) upload(f);
          e.target.value = "";
        }}
      />
      <button
        data-testid="phase-import-btn"
        className="btn btn-ghost"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        style={{ fontSize: 11, padding: "4px 10px" }}
        title="Import a previously exported .rachana package"
      ><Upload size={12} /> Import</button>

      {next && (
        <button
          data-testid="phase-advance-btn"
          className="btn btn-primary"
          onClick={advance}
          disabled={busy}
          style={{ fontSize: 11, padding: "4px 10px" }}
          title={`Continue to ${PHASE_LABELS[next]}`}
        >{busy ? <Loader2 className="pf-spin" size={12}/> : <ChevronRight size={12} />}
          Continue → {PHASE_LABELS[next]}</button>
      )}

      {toast && (
        <div data-testid={`phase-toast-${toast.kind}`} style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: toast.kind === "ok" ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)",
          border: `1px solid ${toast.kind === "ok" ? "#22c55e" : "#ef4444"}`,
          color: toast.kind === "ok" ? "#86efac" : "#fca5a5",
          padding: "8px 16px", fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
          zIndex: 10001, borderRadius: 3, maxWidth: "80vw",
        }}>{toast.text}</div>
      )}
    </div>
  );
}
